import type { ColumnDef, RowSelectionState } from '@tanstack/react-table';
import { Inbox, Loader2, SearchX } from 'lucide-react';

import { Checkbox } from '@/components/shadcn/ui/checkbox';
import type { ScrapingJob } from '@/features/Scraping/api/dto';
import { cn } from '@/lib/cn';
import { DataTable } from '@/shared/ui/data-table';
import { Tooltip } from '@/shared/ui/tooltip';
import { getSourceIcon } from '@/types/feedbackConst';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

type ScrapingJobTableProps = {
  jobs: ScrapingJob[];
  rowSelection: RowSelectionState;
  onClickRow: (job: ScrapingJob) => void;
  onToggleAll: () => void;
  isAllSelected: boolean;
  isIndeterminate: boolean;
  isLoading: boolean;
  isFetchingMore?: boolean;
  hasSearchTerm?: boolean;
  /** email → 이름 매핑 */
  memberNameMap: Map<string, string>;
};

/** 날짜를 워크스페이스 타임존 적용 YYYY-MM-DD로 포맷 */
const formatDate = (date: string | undefined) => {
  if (!date) return null;
  return getTimezoneDayjs(date).format(DATE_FORMAT.base);
};

export const ScrapingJobTable = ({
  jobs,
  rowSelection,
  onClickRow,
  onToggleAll,
  isAllSelected,
  isIndeterminate,
  isLoading,
  isFetchingMore,
  hasSearchTerm,
  memberNameMap,
}: ScrapingJobTableProps) => {
  const columns: ColumnDef<ScrapingJob, unknown>[] = [
    {
      id: 'select',
      header: () => (
        <Checkbox
          checked={isIndeterminate ? 'indeterminate' : isAllSelected}
          onCheckedChange={onToggleAll}
          aria-label="Select all jobs"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={!!rowSelection[row.original.alias]}
          className="pointer-events-none"
        />
      ),
      size: 40,
      meta: { className: 'px-2' },
    },
    {
      id: 'source',
      header: '',
      cell: ({ row }) => getSourceIcon(row.original.source_type, 16),
      size: 32,
      meta: { className: 'px-1' },
    },
    {
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row }) => (
        <Tooltip
          content={<p className="max-w-[200px]">{row.original.title}</p>}
          disabled={!row.original.title}
        >
          <span className="block truncate">{row.original.title}</span>
        </Tooltip>
      ),
      meta: { className: 'flex-1' },
    },
    {
      id: 'product',
      header: 'Product',
      cell: ({ row }) => {
        const name = row.original.scraper_input?.product_name;
        return (
          <Tooltip
            content={<p className="max-w-[200px]">{name}</p>}
            disabled={!name}
          >
            <span className="block truncate">{name ?? '-'}</span>
          </Tooltip>
        );
      },
      meta: { className: 'flex-1 text-muted-foreground' },
    },
    {
      id: 'createdAt',
      header: 'Requested by',
      cell: ({ row }) => {
        return row.original.created_at
          ? formatDate(row.original.created_at)
          : '-';
      },
      size: 120,
      meta: { className: 'text-muted-foreground' },
    },
    {
      id: 'createdBy',
      header: 'Created by',
      cell: ({ row }) => {
        const userId = row.original.updated_by;
        const name = memberNameMap.get(userId);
        return (
          <Tooltip content={name ?? userId} disabled={!name}>
            <span className="block truncate">{name ?? '-'}</span>
          </Tooltip>
        );
      },
      size: 120,
      meta: { className: 'text-muted-foreground' },
    },
  ];

  const emptyState = (
    <div className="flex flex-col items-center justify-center gap-1.5 py-10">
      {hasSearchTerm ? (
        <>
          <SearchX className="size-5 text-muted-foreground/60" />
          <p className="text-xs text-muted-foreground">
            No jobs matching your search
          </p>
        </>
      ) : (
        <>
          <Inbox className="size-5 text-muted-foreground/60" />
          <p className="text-xs text-muted-foreground">
            No scraping jobs found
          </p>
        </>
      )}
    </div>
  );

  return (
    <div className="max-h-96 h-96 overflow-hidden overflow-y-auto rounded-md border border-input">
      <DataTable
        columns={columns}
        data={jobs}
        loading={isLoading}
        getRowId={(row) => row.alias}
        onClickRow={onClickRow}
        emptyState={emptyState}
        size="sm"
        headerClassName="sticky top-0 z-10"
        rowClassName={(row) =>
          cn(
            'select-none transition-colors',
            rowSelection[row.original.alias]
              ? 'bg-primary/5 hover:bg-primary/10'
              : 'hover:bg-muted/50',
          )
        }
      />
      {isFetchingMore && (
        <div className="flex items-center justify-center gap-1.5 py-3 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          Loading more...
        </div>
      )}
    </div>
  );
};
